import { SCOPED_ACCESS_CURRENT } from '../services/scoped-access/scoped-access-request';
import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SessionService } from '../services/session/session.service';
import { AppSettings } from '../app.settings';
import { TabStorageService } from '../services/tabstorage/tabstorage.service';

@Injectable()
export class HttpInterceptorProvider implements HttpInterceptor {
  private unauthorizedRedirectInProgress = false;
  private jwtHelper = new JwtHelperService();

  constructor(
    private injector: Injector,
    private tabStorageService: TabStorageService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const scopedCurrent = request.context.get(SCOPED_ACCESS_CURRENT);
    if (scopedCurrent && !scopedCurrent()) {
      return throwError(new Error('Scoped request context changed'));
    }
    let requestToHandle = request;

    if (request.headers.has('x-global-configs-auth') || request.url.includes('globalconfig.api')) {
      return this.handleResponse(next.handle(requestToHandle), request.url, requestToHandle, next);
    }

    const token = this.getToken(request.url);
    if (token && this.shouldApplyStoredToken(request)) {
      requestToHandle = request.clone({
        headers: request.headers.set('Authorization', 'Bearer ' + token),
      });
    }

    return this.handleResponse(next.handle(requestToHandle), request.url, requestToHandle, next);
  }

  private getToken(route: string): string | null {
    if (route.includes('infra-mgmt.api')) {
      return this.tabStorageService.getStorage(TabStorageService.INFRA_MGMT_TOKEN);
    }
    if (route.includes('be-playground.api')) {
      return localStorage.getItem('playground-token');
    }
    return this.tabStorageService.getStorage(TabStorageService.JWT_TOKEN);
  }

  private shouldApplyStoredToken(request: HttpRequest<any>): boolean {
    return (
      !request.headers.has('Authorization') ||
      this.isWallitApiRequest(request.url) ||
      request.url.includes('infra-mgmt.api') ||
      request.url.includes('be-playground.api')
    );
  }

  private shouldAttemptRefresh(error: HttpErrorResponse, request: HttpRequest<any>, requestUrl: string): boolean {
    return Boolean(
      error &&
      error.status === 401 &&
      this.isWallitApiRequest(requestUrl) &&
      !this.isRefreshExcludedRequest(request, requestUrl) &&
      this.tabStorageService.getStorage(TabStorageService.JWT_TOKEN) &&
      this.hasRequiredExpirationClaim(this.tabStorageService.getStorage(TabStorageService.JWT_TOKEN)) &&
      this.tabStorageService.getStorage(TabStorageService.REFRESH_TOKEN) &&
      !this.injector.get(SessionService).isAbsoluteSessionExpired()
    );
  }

  private isWallitApiRequest(requestUrl: string): boolean {
    if (!/^https?:\/\//i.test(requestUrl)) {
      return true;
    }

    try {
      const requestHost = new URL(requestUrl).host;
      const endpointHost = new URL(AppSettings.getEndpoint()).host;
      return requestHost === endpointHost || requestHost.endsWith('.api.wallit.app');
    } catch (_) {
      return requestUrl.startsWith(AppSettings.getEndpoint());
    }
  }

  private isRefreshExcludedRequest(request: HttpRequest<any>, requestUrl: string): boolean {
    return (
      request.headers.has('x-global-configs-auth') ||
      requestUrl.includes('globalconfig.api') ||
      requestUrl.includes('infra-mgmt.api') ||
      requestUrl.includes('be-playground.api') ||
      requestUrl.includes('/auth')
    );
  }

  private withCurrentJwtToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.tabStorageService.getStorage(TabStorageService.JWT_TOKEN);
    return token
      ? request.clone({headers: request.headers.set('Authorization', 'Bearer ' + token)})
      : request;
  }

  private shouldForceLogout(error: HttpErrorResponse, requestUrl: string): boolean {
    if (!error) {
      return false;
    }

    if (error.status === 401) {
      return true;
    }

    if (error.status === 403) {
      return !this.hasValidStoredToken(error.url || requestUrl);
    }

    return false;
  }

  private hasValidStoredToken(route: string): boolean {
    const token = this.getToken(route);
    if (!token) {
      return false;
    }

    try {
      const sessionService = this.injector.get(SessionService);
      return this.hasRequiredExpirationClaim(token)
        && !sessionService.isAbsoluteSessionExpired(token)
        && !this.jwtHelper.isTokenExpired(token);
    } catch (_) {
      return false;
    }
  }

  private hasRequiredExpirationClaim(token: string | null): boolean {
    if (!token) {
      return false;
    }

    const decodedToken = this.jwtHelper.decodeToken(token);
    return Boolean(decodedToken && Number.isFinite(Number(decodedToken.exp)));
  }

  private forceLogout(): void {
    if (this.unauthorizedRedirectInProgress) {
      return;
    }
    this.unauthorizedRedirectInProgress = true;
    const sessionService = this.injector.get(SessionService);
    sessionService.logout();
    window.location.assign('/login/false');
  }

  private handleResponse(
    response: Observable<HttpEvent<any>>,
    requestUrl: string,
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const scopedCurrent = request.context.get(SCOPED_ACCESS_CURRENT);
    // Public/auth and independently authenticated services keep their established error handling.
    const sessionBound = scopedCurrent || (this.getToken(requestUrl) && this.isWallitApiRequest(requestUrl)
      && !this.isRefreshExcludedRequest(request, requestUrl));
    // Independent configuration loads during SessionService construction; classify before resolving it.
    const credentialContext = sessionBound ? this.injector.get(SessionService).captureCredentialContext() : undefined;
    const sessionCurrent = credentialContext ? credentialContext.current : () => true;
    const current = () => sessionCurrent() && (!scopedCurrent || scopedCurrent());
    return response.pipe(
      catchError((error: HttpErrorResponse) => {
        if (!current()) { return throwError(new Error('Request session context changed')); }
        if (this.shouldAttemptRefresh(error, request, requestUrl)) {
          let retryRequest = request;
          return from(this.injector.get(SessionService).refreshToken(current, credentialContext)).pipe(
            switchMap(() => {
              if (!current()) { return throwError(new Error('Request session context changed')); }
              retryRequest = this.withCurrentJwtToken(request);
              return next.handle(retryRequest);
            }),
            catchError((refreshError: HttpErrorResponse) => {
              // A late failure belongs to the token actually sent, not a newer session/token.
              const sameCredential = retryRequest.headers.get('Authorization') === `Bearer ${this.getToken(requestUrl)}`;
              if (current() && sameCredential && (!scopedCurrent || this.shouldForceLogout(refreshError, requestUrl))) {
                this.forceLogout();
              }
              return throwError(refreshError);
            }),
          );
        }
        if (this.shouldForceLogout(error, requestUrl)) { this.forceLogout(); }
        return throwError(error);
      }),
    );
  }
}
